import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiArrowLeft,
  FiClock,
  FiCheck,
  FiTruck,
  FiX,
  FiChevronRight,
  FiRefreshCw,
} from 'react-icons/fi';
import { IoRestaurantOutline, IoBicycleOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { Card, LoaderComponent, EmptyState } from '../../components/ui';
import { api } from '../../services/api';
import { fmtInstante } from '../../utils/tempoCv';
import { useToast } from '../../contexts';
import { useFoodCart } from '../../contexts/FoodCartContext';

interface OrderItem {
  id: number;
  menuItemId: number;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  itemName: string;
}

interface Order {
  id: number;
  restaurantId: number;
  restaurantName: string;
  type: 'DELIVERY' | 'LOCAL' | 'TAKEAWAY';
  status: string;
  subtotal: number;
  deliveryFee: number;
  total: number;
  deliveryAddress?: string;
  createdAt: string;
  items: OrderItem[];
  /** NULL = não se aplica (mesa/staff); PENDENTE | PROCESSANDO | FALHADO = por pagar; PAGO */
  paymentStatus?: string | null;
}

const porPagar = (o: Order) => ['PENDENTE', 'FALHADO', 'PROCESSANDO'].includes(o.paymentStatus || '');

type TabType = 'active' | 'history';

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  PENDENTE: { label: 'Pendente', color: 'text-yellow-500 bg-yellow-100', icon: <FiClock /> },
  CONFIRMADO: { label: 'Confirmado', color: 'text-blue-500 bg-blue-100', icon: <FiCheck /> },
  PREPARANDO: { label: 'Preparando', color: 'text-pagali-purple bg-pagali-purple/10', icon: <IoRestaurantOutline /> },
  PRONTO: { label: 'Pronto', color: 'text-green-500 bg-green-100', icon: <FiCheck /> },
  ENTREGANDO: { label: 'A Caminho', color: 'text-purple-500 bg-purple-100', icon: <IoBicycleOutline /> },
  ENTREGUE: { label: 'Entregue', color: 'text-green-600 bg-green-100', icon: <IoCheckmarkCircle /> },
  CANCELADO: { label: 'Cancelado', color: 'text-red-500 bg-red-100', icon: <FiX /> },
};

const FoodOrders: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [refreshing, setRefreshing] = useState(false);

  const { showSuccess, showError } = useToast();
  const foodCart = useFoodCart();
  const [aPagar, setAPagar] = useState<number | null>(null);
  const [destaque, setDestaque] = useState<number | null>(location.state?.newOrderId ?? null);
  const newOrderId = destaque;

  // Regresso da página da SISP: /food/orders?pedido=<id>&status=success|failed
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const pedido = Number(q.get('pedido'));
    const st = q.get('status');
    if (!pedido || !st) return;
    setDestaque(pedido);
    if (st === 'success') {
      foodCart.clearCart();
      showSuccess('Pagamento confirmado! O restaurante já recebeu o seu pedido.');
    } else {
      showError('O pagamento não foi concluído. Pode tentar outra vez com «Pagar».');
    }
    navigate('/food/orders', { replace: true, state: { newOrderId: pedido } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pagar = async (orderId: number) => {
    try {
      setAPagar(orderId);
      const r = await api.post(`/food/orders/${orderId}/pay`, {
        metodo: 'SISP',
        returnUrl: `${window.location.origin}/food/orders?pedido=${orderId}`,
      });
      window.location.assign(r.data.data.paymentUrl);
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Não foi possível iniciar o pagamento');
      setAPagar(null);
    }
  };

  useEffect(() => {
    fetchOrders();

    // Auto-refresh for active orders
    const interval = setInterval(() => {
      if (activeTab === 'active') {
        fetchOrders(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchOrders = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      const response = await api.get('/food/orders');
      setOrders(response.data.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const activeOrders = orders.filter(
    (o) => !['ENTREGUE', 'CANCELADO'].includes(o.status)
  );
  const historyOrders = orders.filter((o) =>
    ['ENTREGUE', 'CANCELADO'].includes(o.status)
  );

  const displayOrders = activeTab === 'active' ? activeOrders : historyOrders;

  const formatPrice = (price: number | string) => `${Number(price || 0).toFixed(0)}$`;
  const formatDate = (dateStr: string) => {
    return fmtInstante(dateStr, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }, 'pt-PT');
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'DELIVERY': return 'Entrega';
      case 'LOCAL': return 'No Local';
      case 'TAKEAWAY': return 'Levantar';
      default: return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white px-4 py-4 flex items-center gap-4 border-b sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <FiArrowLeft />
        </button>
        <h1 className="font-semibold text-gray-800 flex-1">Meus Pedidos</h1>
        <button
          onClick={() => fetchOrders()}
          className={`w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center ${
            refreshing ? 'animate-spin' : ''
          }`}
        >
          <FiRefreshCw />
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white px-4 py-2 border-b">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'active'
                ? 'bg-pagali-purple text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Em Andamento ({activeOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'history'
                ? 'bg-pagali-purple text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Histórico ({historyOrders.length})
          </button>
        </div>
      </div>

      {/* New Order Success Message */}
      {newOrderId && (
        <div className="mx-4 mt-4">
          <Card className="bg-green-50 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                <IoCheckmarkCircle className="text-xl" />
              </div>
              <div>
                <p className="font-medium text-green-800">Pedido Realizado!</p>
                <p className="text-sm text-green-600">
                  Pedido #{newOrderId} - Acompanhe o status abaixo
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Orders List */}
      <div className="px-4 py-4">
        {loading ? (
          <LoaderComponent />
        ) : displayOrders.length === 0 ? (
          <EmptyState
            icon={<IoRestaurantOutline className="text-4xl" />}
            title={
              activeTab === 'active'
                ? 'Nenhum pedido em andamento'
                : 'Nenhum pedido no histórico'
            }
            description={
              activeTab === 'active'
                ? 'Explore restaurantes e faça seu primeiro pedido!'
                : 'Seus pedidos finalizados aparecerão aqui'
            }
          />
        ) : (
          <div className="space-y-4">
            {displayOrders.map((order) => {
              const status = statusConfig[order.status] || statusConfig.PENDENTE;
              const isHighlighted = newOrderId === order.id;

              return (
                <Card
                  key={order.id}
                  className={`cursor-pointer hover:shadow-md transition-all ${
                    isHighlighted ? 'ring-2 ring-green-500' : ''
                  }`}
                  onClick={() => navigate(`/food/orders/${order.id}`)}
                >
                  {/* Order Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-pagali-purple/10 rounded-full flex items-center justify-center">
                        <IoRestaurantOutline className="text-pagali-purple" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">
                          {order.restaurantName}
                        </h3>
                        <p className="text-xs text-gray-500">
                          #{order.id} • {formatDate(order.createdAt)}
                        </p>
                      </div>
                    </div>
                    <FiChevronRight className="text-gray-400" />
                  </div>

                  {/* Order Status */}
                  {porPagar(order) && order.status === 'PENDENTE' ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm text-amber-700">
                        <FiClock />
                        <span className="font-medium">Por pagar</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); pagar(order.id); }}
                        disabled={aPagar === order.id}
                        className="rounded-full bg-pagali-purple px-4 py-1 text-sm font-semibold text-white hover:bg-pagali-purple-light disabled:opacity-60"
                      >
                        {aPagar === order.id ? 'A abrir…' : 'Pagar'}
                      </button>
                    </div>
                  ) : (
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${status.color}`}>
                      {status.icon}
                      <span className="font-medium">{status.label}</span>
                    </div>
                  )}

                  {/* Order Items Preview */}
                  <div className="mt-3 text-sm text-gray-600">
                    {order.items.slice(0, 2).map((item, idx) => (
                      <span key={item.id}>
                        {idx > 0 && ', '}
                        {item.quantity}x {item.itemName}
                      </span>
                    ))}
                    {order.items.length > 2 && (
                      <span className="text-gray-400">
                        {' '}+{order.items.length - 2} mais
                      </span>
                    )}
                  </div>

                  {/* Order Footer */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <span className="text-sm text-gray-500">
                      {getTypeLabel(order.type)}
                    </span>
                    <span className="font-semibold text-pagali-purple">
                      {formatPrice(order.total)}
                    </span>
                  </div>

                  {/* Progress Bar for Active Orders */}
                  {activeTab === 'active' && !['CANCELADO'].includes(order.status) && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        {['PENDENTE', 'CONFIRMADO', 'PREPARANDO', 'PRONTO', 'ENTREGANDO', 'ENTREGUE'].map(
                          (step, idx, arr) => {
                            const currentIdx = arr.indexOf(order.status);
                            const isCompleted = idx <= currentIdx;
                            const isCurrent = idx === currentIdx;

                            if (order.type !== 'DELIVERY' && step === 'ENTREGANDO') {
                              return null;
                            }

                            return (
                              <div
                                key={step}
                                className={`flex flex-col items-center ${
                                  isCurrent ? 'text-pagali-purple' : isCompleted ? 'text-green-500' : ''
                                }`}
                              >
                                <div
                                  className={`w-3 h-3 rounded-full ${
                                    isCompleted
                                      ? 'bg-green-500'
                                      : isCurrent
                                      ? 'bg-pagali-purple'
                                      : 'bg-gray-300'
                                  }`}
                                />
                              </div>
                            );
                          }
                        )}
                      </div>
                      <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-pagali-purple to-pagali-primary transition-all"
                          style={{
                            width: `${
                              ((['PENDENTE', 'CONFIRMADO', 'PREPARANDO', 'PRONTO', 'ENTREGANDO', 'ENTREGUE'].indexOf(
                                order.status
                              ) +
                                1) /
                                6) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Action Button */}
      <div className="fixed bottom-20 left-0 right-0 px-4">
        <button
          onClick={() => navigate('/food')}
          className="w-full bg-pagali-purple text-white py-4 rounded-xl font-semibold shadow-lg shadow-pagali-purple/30"
        >
          Novo Pedido
        </button>
      </div>
    </div>
  );
};

export default FoodOrders;
